/**
 * Skeleton placeholder mirroring TicketRowItem's layout. Used while a page
 * is loading (initial mount + pagination) so the list area never collapses
 * to a blank/spinner state — progressive disclosure feels faster than
 * "blank screen → snap to data".
 *
 * Keep visual shape in sync with TicketRowItem so the swap from skeleton
 * → real row doesn't reflow the layout (no jump).
 */

interface TicketRowSkeletonProps {
  /** Vary the inner bar width so a stack of skeletons doesn't look like a
   *  perfectly aligned grid. `seed` shifts the widths deterministically — no
   *  Math.random so SSR / hydration stays stable. */
  seed?: number;
}

export function TicketRowSkeleton({ seed = 0 }: TicketRowSkeletonProps) {
  // Deterministic width variants — pick from a small fixed set so widths
  // look natural across 8+ stacked rows without flickering during re-render.
  const nameW = ['w-28', 'w-32', 'w-36', 'w-24', 'w-40'][seed % 5];
  const metaW = ['w-32', 'w-40', 'w-36', 'w-28', 'w-44'][(seed + 2) % 5];

  return (
    <div
      className="w-full bg-white border border-black/10 rounded-[8.75px] p-[10.667px] flex items-center gap-2.5 animate-pulse"
      aria-hidden
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3">
          {/* Ticket-ID badge placeholder — same footprint as the real chip */}
          <div className="h-4 w-16 rounded-md bg-surface-tertiary shrink-0" />
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            {/* Name placeholder */}
            <div className={`h-4 ${nameW} rounded-md bg-surface-tertiary`} />
            {/* Phone + relative time placeholder */}
            <div className={`h-3 ${metaW} rounded-md bg-surface-tertiary`} />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 shrink-0">
        {/* Status badge placeholder */}
        <div className="h-5 w-14 rounded-full bg-surface-tertiary" />
      </div>
    </div>
  );
}

interface TicketRowSkeletonListProps {
  /** Number of skeleton rows to render. Default 8 fills the typical mobile
   *  viewport without scrolling. */
  count?: number;
}

export function TicketRowSkeletonList({ count = 8 }: TicketRowSkeletonListProps) {
  return (
    <div className="flex flex-col gap-1 pb-2" role="status" aria-label="Loading tickets">
      {Array.from({ length: count }, (_, i) => (
        <TicketRowSkeleton key={i} seed={i} />
      ))}
    </div>
  );
}
